export default function QuadratureExplanation() {
  return (
    <div className="w-full p-1 text-xs text-gray-800 text-justify leading-relaxed hyphens-auto md:text-sm">
      <p>
        <strong>Goal:</strong> Approximate the value of a definite integral for a function <code>f: ℝ → ℝ </code> over
        an interval <code>[a,b]</code>:
      </p>
      <pre className="bg-white p-2 border border-gray-200 rounded text-xs mt-2 mb-4 text-center leading-tight">
       <p className="text-center text-xs md:text-sm">
            I(f) = ∫<sub>a</sub><sup>b</sup> f(x)
        </p>

      </pre>

      <p className="mt-3"><strong>Mathematical explanation</strong></p>
      <p className="mb-3">
        Numerical quadrature approximates the integral by a weighted sum of function evaluations:
        <pre className="bg-white p-2 border border-gray-200 rounded text-xs text-center mt-2 mb-2">
          I(f) ≈ ∑ wᵢ · f(xᵢ)
        </pre>
        where the <code>xᵢ</code> are specific sample points and <code>wᵢ</code> are the corresponding weights determined by the method.
      </p>

      <ul className="list-disc pl-5 space-y-2 mb-4">
        <li>
          <strong>Midpoint (Box) Rule:</strong> Uses the midpoint of each subinterval to estimate the area under the curve using rectangles:
          <pre className="bg-white p-2 border border-gray-200 rounded text-xs text-center">∑ f(xᵢ) · Δx</pre>
        </li>
        <li>
          <strong>Trapezoid Rule:</strong> Approximates the area using trapezoids between each pair of adjacent points:
          <pre className="bg-white p-2 border border-gray-200 rounded text-xs text-center">∑ (f(xᵢ) + f(xᵢ₊₁)) · Δx / 2</pre>
        </li>
        <li>
          <strong>Simpson’s Rule:</strong> Fits parabolas over each pair of subintervals and uses a weighted average:
          <pre className="bg-white p-2 border border-gray-200 rounded text-xs text-center">∑ (f(x₀) + 4f(x₁) + f(x₂)) · Δx / 3</pre>
          Requires an even number of subintervals.
        </li>
      </ul>

      <p className="mt-3"><strong>Composite (Summed) Rules:</strong></p>
      <p>
        If enabled, the interval <code>[a, b]</code> is split into <code>n</code> subintervals, and the selected rule is applied over each subinterval for improved accuracy.
      </p>

      <p className="mt-4 font-semibold">Input format</p>
      <ul className="list-disc pl-5 space-y-1 mb-2">
        <li><strong>Function:</strong> Python-style expression in <code>x</code>, e.g. <code>x**2 - 4*x</code></li>
        <li><strong>Bounds:</strong> Numeric values <code>a</code> and <code>b</code> (must not be equal)</li>
        <li><strong>Subintervals (optional):</strong> Positive integer if composite rule is selected</li>
      </ul>
      <pre className="bg-white p-2 border border-gray-200 rounded text-xs mt-1 mb-2">
        f(x): x**3 - 3*x**2 +5<br />
        [a,b]: [0,4]
      </pre>

      <p className="mt-4 font-semibold">Output format</p>
      <ul className="list-disc pl-5 mb-4">
        <li>Approximate integral value</li>
        <li>Used sample points and subinterval width <code>Δx</code></li>
        <li>Function plot including rectangles/trapezoids/parabolas for visualization</li>
        <li>Error message if function parsing or evaluation fails</li>
      </ul>
    </div>
  );
}
